import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X } from "lucide-react";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "custom", label: "Custom range" },
];

export default function FiltersBar({ filters, setFilters }) {
  const { data: models = [] } = useQuery({ queryKey: ["models"], queryFn: async () => (await api.get("/models")).data });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: tokens = [] } = useQuery({ queryKey: ["ingest-tokens"], queryFn: async () => (await api.get("/ingest-tokens")).data });

  const tokenLabels = ["default (env)", ...tokens.map((t) => t.label)];
  const revokedLabels = new Set(tokens.filter((t) => !t.active).map((t) => t.label));

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === "__all__" ? null : v }));
  const active = filters.user || filters.model || filters.session || filters.token;

  const selCls = "h-9 w-[150px] rounded-none bg-black/50 border-border font-mono text-xs focus:ring-1 focus:ring-white";
  const dateCls = "h-9 w-[150px] rounded-none bg-black/50 border-border font-mono text-xs focus:ring-1 focus:ring-white";

  return (
    <div className="sticky top-14 z-40 bg-background border-b border-border px-4 lg:px-6 py-2.5 flex flex-wrap items-center gap-3" data-testid="filters-bar">
      <div className="flex items-center gap-2 text-muted-foreground">
        <SlidersHorizontal className="w-4 h-4" />
        <span className="overline">filters</span>
      </div>

      <Select value={filters.range} onValueChange={(v) => set("range", v)}>
        <SelectTrigger className={selCls} data-testid="filter-range-select"><SelectValue /></SelectTrigger>
        <SelectContent className="rounded-none font-mono">
          {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
        </SelectContent>
      </Select>

      {filters.range === "custom" && (
        <div className="flex items-center gap-2" data-testid="custom-range-inputs">
          <Input
            type="date"
            value={filters.from || ""}
            max={filters.to || undefined}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            data-testid="filter-from-date"
            className={dateCls}
          />
          <span className="text-muted-foreground font-mono text-xs">→</span>
          <Input
            type="date"
            value={filters.to || ""}
            min={filters.from || undefined}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            data-testid="filter-to-date"
            className={dateCls}
          />
          {!(filters.from && filters.to) && (
            <span className="font-mono text-[11px] text-amber-500/80" data-testid="custom-range-hint">
              pick both dates (showing this week meanwhile)
            </span>
          )}
        </div>
      )}

      <Select value={filters.user || "__all__"} onValueChange={(v) => set("user", v)}>
        <SelectTrigger className={selCls} data-testid="filter-user-select"><SelectValue placeholder="All users" /></SelectTrigger>
        <SelectContent className="rounded-none font-mono">
          <SelectItem value="__all__">All users</SelectItem>
          {users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.model || "__all__"} onValueChange={(v) => set("model", v)}>
        <SelectTrigger className={selCls} data-testid="filter-model-select"><SelectValue placeholder="All models" /></SelectTrigger>
        <SelectContent className="rounded-none font-mono">
          <SelectItem value="__all__">All models</SelectItem>
          {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.token || "__all__"} onValueChange={(v) => set("token", v)}>
        <SelectTrigger className={selCls} data-testid="filter-token-select"><SelectValue placeholder="All tokens" /></SelectTrigger>
        <SelectContent className="rounded-none font-mono">
          <SelectItem value="__all__">All tokens</SelectItem>
          {tokenLabels.map((t) => <SelectItem key={t} value={t}>{t}{revokedLabels.has(t) ? " (revoked)" : ""}</SelectItem>)}
        </SelectContent>
      </Select>

      {filters.session && (
        <div className="flex items-center gap-2 h-9 px-3 border border-blue-500/40 bg-blue-500/10 font-mono text-xs text-blue-400" data-testid="filter-session-chip">
          session: {filters.session}
          <button onClick={() => set("session", "__all__")} data-testid="clear-session-filter"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {active && (
        <Button
          variant="ghost" size="sm"
          onClick={() => setFilters((f) => ({ range: f.range, from: f.from, to: f.to, user: null, model: null, session: null, token: null }))}
          data-testid="clear-filters-button"
          className="rounded-none font-mono text-xs text-muted-foreground hover:text-white ml-auto"
        >
          Clear all
        </Button>
      )}
    </div>
  );
}
